import { useQuery } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { knowledgeBaseWorkspacesQuery } from '../../api/knowledgeBaseWorkspaces.api';
import { useKnowledgeBaseModal } from '../knowledge-base-modal.store';
import type { KnowledgeBaseDto } from '../knowledge-bases.types';

/**
 * Whether knowledge bases can be shared with chosen workspaces, and the workspaces the one being
 * edited is shared with. The summary names only the first of them, so the rest load separately.
 *
 * Only the surface with a picker reads it. The one without keeps the single-workspace reading, so
 * it neither seeds an option it cannot show nor saves a choice that would drop the other workspaces.
 */
export const useKbWorkspaceSelection = (knowledgeBase: KnowledgeBaseDto | undefined) => {
  const canSelectWorkspaces = useBooleanFlagValue('kb_workspace_selection', false);
  const { knowledgeBaseModalTypeEdit } = useKnowledgeBaseModal();

  const { data: scope } = useQuery({
    ...knowledgeBaseWorkspacesQuery(knowledgeBase?.id),
    // Loaded for an organization-wide knowledge base too: it keeps the workspaces it was shared
    // with, so switching back to chosen workspaces can offer them again.
    enabled:
      canSelectWorkspaces &&
      knowledgeBaseModalTypeEdit &&
      !!knowledgeBase?.id &&
      !knowledgeBase.isInternal,
  });

  return { canSelectWorkspaces, scope };
};
